import React, { useState, useEffect } from "react";
import Styles from "./EditLocation.module.css";
import { Input, Button, Modal } from "@sainsburys-tech/fable";
import Headerfile from "../../common/Headerfile";
import Footerfile from "../../common/Footerfile";
import { alpha, styled } from "@mui/material/styles";
import { pink } from "@mui/material/colors";
import Switch from "@mui/material/Switch";
import { useGetPostsQuery, usePatchDataMutation } from "../../../api/apiConfig";
import { useNavigate, useParams } from "react-router-dom";
import { getLogisticsRole } from "../../../utils/authToken";
import { USER_ROLE } from "../../../constants/roles";
import Unauthorized from "../../unauthorized/Unauthorized";

interface IState {
  LocationId: string;
  Name: string;
  Status: boolean;
  PlaceId: string;
  MessageId: string;
  brand: [string];
  Code: string;
  Type: string;
  PostCode: string;
  ProcessedDateTime: Date;
  OpenDate: Date;
  CloseDate: Date;
  Address1: string;
  City: string;
  County: string;
  CountryCode: string;
}

interface IErrors {
  Id?: string;
  Name?: string;
  Status?: boolean;
  PlaceId?: string;
  MessageId?: string;
  brand?: [string];
  Code?: string;
  Type?: string;
  PostCode?: string;
  ProcessedDateTime?: Date;
  OpenDate?: Date;
  CloseDate?: Date;
  Address1?: string;
  City?: string;
  County?: string;
  CountryCode?: string;
}

interface IData {
  StatusCode: number;
  Status: string;
  TotalRecords?: number;
  Message?: string;
  Model?: any;
  Value?: string;
}

interface IResponse {
  data: IData;
  TotalRecords: number;
}

const initialState: IState = {
  LocationId: "",
  Name: "",
  Status: false,
  PlaceId: "",
  MessageId: "",
  brand: [""],
  Code: "",
  Type: "",
  PostCode: "",
  ProcessedDateTime: new Date(),
  OpenDate: new Date(),
  CloseDate: new Date(),
  Address1: "",
  City: "",
  County: "",
  CountryCode: ""
};

const Location = () => {
  const [values, setValues] = useState<IState>(initialState);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState<IErrors>({});
  const label = { inputProps: { "aria-label": "Color switch demo" } };
  const [apiHeading, setApiHeading] = useState<string | null>(null);
  const [apiMessage, setApiMessage] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(false);

  const { Id } = useParams();

  const loggedUserRole = getLogisticsRole();

  const isDisabled =
    loggedUserRole?.includes(USER_ROLE.ADMIN) ||
    loggedUserRole?.includes(USER_ROLE.READ_WRITE)
      ? false
      : true;

  const {
    isSuccess,
    data: getData,
    refetch
  } = useGetPostsQuery(`locations/${Id}`);
  const [patchData] = usePatchDataMutation();

  const handleToggleChange = (name: string) => (event: any) => {
    handleChange(name, event.target.checked);
  };

  const handleChange = (name: string, value: string | boolean) => {
    setValues({ ...values, [name]: value });
  };

  useEffect(() => {
    refetch();
  }, []);
  useEffect(() => {
    if (isSuccess && getData?.Model) {
      const model = getData.Model as { [key: string]: any }; // Type assertion

      setValues({
        LocationId: model?.LocationId,
        Name: model?.Name,
        Status: model?.Status === "Active",
        Code: model?.Code,
        PlaceId: model?.PlaceId,
        MessageId: model?.MessageId,
        brand: model?.brand,
        PostCode: model.PostCode,
        ProcessedDateTime: model.ProcessedDateTime,
        OpenDate: model?.OpenDate,
        CloseDate: model?.ClosedDate,
        Address1: model?.Address1,
        City: model?.City,
        County: model?.Country,
        CountryCode: model?.CountryCode,
        Type: model?.Type
      });
    } else {
      console.log("No data found or error occurred");
    }
  }, [isSuccess, getData]);

  const Android12Switch = styled(Switch)(({ theme }) => ({
    padding: 8,
    "& .MuiSwitch-switchBase.Mui-checked": {
      color: "#f06c00",
      "&:hover": {
        backgroundColor: alpha(pink[600], theme.palette.action.hoverOpacity)
      }
    },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#84776c"
    },
    "& .MuiSwitch-track": {
      borderRadius: 22 / 2,
      "&::before, &::after": {
        content: '""',
        position: "absolute",
        top: "50%",
        transform: "translateY(-50%)",
        width: 16,
        height: 16
      },
      "&::before": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main)
        )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
        left: 12
      },
      "&::after": {
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
          theme.palette.getContrastText(theme.palette.primary.main)
        )}" d="M19,13H5V11H19V13Z" /></svg>')`,
        right: 12
      }
    },
    "& .MuiSwitch-thumb": {
      boxShadow: "none",
      width: 16,
      height: 16,
      margin: 2
    }
  }));

  const validate = (values: IState): IErrors => {
    let errors: IErrors = {};

    if (!values.Name) {
      errors.Name = "Location Name is required";
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitted(true);
    const validationErrors = validate(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length === 0) {
      setIsSaveButtonDisabled(true);
      try {
        const response = await patchData({
          Method: `locations/${Id}`,
          Name: values.Name,
          Status: values.Status ? "Active" : "Inactive"
        });
        if (
          isIResponse(response) &&
          response.data.StatusCode === 0 &&
          response.data.Status === "Ok"
        ) {
          setIsSaveButtonDisabled(false);
          setApiMessage("Location updated successfully" || null);
          setApiHeading("Success!!!");
          setIsOpen(true);
          setValues(initialState);
          setTimeout(() => {
            navigate("/addlocation");
          }, 1500);
        } else if (isIResponse(response) && response.data) {
          setIsSaveButtonDisabled(false);
          setApiMessage(response.data.Message || null);
          setApiHeading("Error!!!");
          setIsOpen(true);
          console.error("Error response:", response);
        } else {
          setIsSaveButtonDisabled(false);
          console.error("Something Went Wrong..", response);
        }
      } catch (error) {
        setIsSaveButtonDisabled(false);
        console.error(error);
      }
    }
  };

  function isIResponse(response: any): response is IResponse {
    return response.data !== undefined && typeof response.data === "object";
  }

  return (
    <>
      <div className={Styles.mainContainer}>
        <Headerfile />
        {isDisabled ? (
          <Unauthorized />
        ) : (
          <form onSubmit={handleSubmit} className={Styles.formContainer}>
            <h5>Edit Location</h5>
            <p className={Styles.rightAlign}>
              <span className={Styles.redAsterisk}>*</span>Indicates required
              field
            </p>{" "}
            <label className={Styles.label}>
              <Input
                name="Id"
                label="Location ID"
                autocomplete="off"
                type="text"
                isReadOnly
                value={values.Code}
                className={Styles.input}
              />
              {isSubmitted && errors.Id && (
                <div className={Styles.error}>{errors.Id}</div>
              )}
            </label>
            <label className={Styles.label}>
              <Input
                name="Name"
                label="Name*"
                autocomplete="off"
                type="text"
                onChange={(e) => handleChange("Name", e)}
                value={values.Name}
                className={Styles.input}
              />
              {isSubmitted && errors.Name && (
                <div className={Styles.error}>{errors.Name}</div>
              )}
            </label>
            <label className={Styles.label}>
              <b>Status</b>
              <Android12Switch
                {...label}
                checked={values.Status}
                onChange={handleToggleChange("Status")}
              />
            </label>
            <Button
              type="submit"
              className={Styles.button}
              disabled={isSaveButtonDisabled}
            >
              Update
            </Button>
            <br />
          </form>
        )}
        <Modal
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          layout="default"
          header={{
            isDismissible: true
          }}
        >
          <div className="text-center fw-400">
            <h3 className="text-center fw-500">{apiHeading}</h3>
            <h5 className="fw-400">{apiMessage}</h5>
          </div>
        </Modal>
      </div>
      <Footerfile />
    </>
  );
};

export default Location;
